import { createContext, useContext, useState, useEffect, ReactNode } from "react";

export type Language = "es" | "en";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// ── Translations ────────────────────────────────────────────────────────────────
const translations: Record<string, Record<Language, string>> = {
  // Navbar
  "nav.home": { es: "Inicio", en: "Home" },
  "nav.faceChart": { es: "Face Chart", en: "Face Chart" },
  "nav.analyzer": { es: "Analizador", en: "Analyzer" },
  "nav.gallery": { es: "Galería", en: "Gallery" },
  "nav.quiz": { es: "Quiz", en: "Quiz" },
  "nav.blog": { es: "Blog", en: "Blog" },
  "nav.history": { es: "Historia", en: "History" },
  "nav.premium": { es: "Premium", en: "Premium" },
  "nav.profile": { es: "Mi Perfil", en: "My Profile" },
  "nav.signOut": { es: "Cerrar Sesión", en: "Sign Out" },
  "nav.signIn": { es: "Iniciar Sesión", en: "Sign In" },
  "nav.showMenu": { es: "Mostrar menú", en: "Show menu" },

  // Index
  "index.heroTitle1": { es: "Generador de Face Chart", en: "Free Face Chart" },
  "index.heroTitle2": { es: "Gratis Online", en: "Generator Online" },
  "index.heroSubtitle": { es: "Crea face charts profesionales, descárgalos en PNG y analiza tu maquillaje con foto.", en: "Create professional face charts, download as PNG and analyze your makeup with a photo." },
  "index.heroSeoLine": { es: "Face chart online gratis · Analizador de maquillaje con foto · Blog de tendencias de maquillaje", en: "Free online face chart · Makeup analyzer with photo · Makeup trends blog" },
  "index.startNow": { es: "Crear Face Chart Gratis", en: "Create Free Face Chart" },
  "index.tryAnalyzer": { es: "Probar Analizador", en: "Try Analyzer" },
  "index.exploreBlog": { es: "Explorar Blog", en: "Explore Blog" },
  "index.featuresTitle1": { es: "Todo lo que necesitas en", en: "Everything you need in" },
  "index.featuresTitle2": { es: "un solo lugar", en: "one place" },
  "index.feat1Title": { es: "Creador de Face Chart", en: "Face Chart Creator" },
  "index.feat1Desc": { es: "Crea face charts de maquillaje profesionales y descárgalos en PNG gratis. Plantilla de face chart descargable para practicar.", en: "Create professional makeup face charts and download them as free PNG. Downloadable face chart template for practice." },
  "index.feat2Title": { es: "Analizador de Maquillaje", en: "Makeup Analyzer" },
  "index.feat2Desc": { es: "Sube tu foto de maquillaje y obtén una puntuación detallada. Analiza maquillaje con foto al instante.", en: "Upload your makeup photo and get a detailed score. Analyze makeup with a photo instantly." },
  "index.feat3Title": { es: "Quiz Interactivo", en: "Interactive Quiz" },
  "index.feat3Desc": { es: "Pon a prueba tus conocimientos con más de 15 preguntas de maquillaje.", en: "Test your knowledge with 15+ makeup questions." },
  "index.feat4Title": { es: "Blog de Maquillaje", en: "Makeup Blog" },
  "index.feat4Desc": { es: "Artículos, tutoriales, maquillaje coreano e ideas de maquillaje con face chart.", en: "Articles, tutorials, Korean makeup and face chart makeup ideas." },
  "index.faqTitle": { es: "Preguntas", en: "Frequently Asked" },
  "index.faqTitleGradient": { es: "Frecuentes", en: "Questions" },
  "index.ctaTitle": { es: "¿Lista para brillar?", en: "Ready to shine?" },
  "index.ctaSubtitle": { es: "Crea tu cuenta gratuita y empieza a usar el generador de face chart gratis y el analizador de maquillaje.", en: "Create your free account and start using the free face chart generator and makeup analyzer." },
  "index.goToProfile": { es: "Ir a Mi Perfil", en: "Go to My Profile" },
  "index.createAccount": { es: "Crear Cuenta Gratis", en: "Create Free Account" },
  "index.alreadyHaveAccount": { es: "¿Ya tienes cuenta? Inicia sesión o recupera tu contraseña", en: "Already have an account? Sign in or recover your password" },

  // Face Chart
  "fc.title": { es: "Crear Face Chart de", en: "Create Makeup" },
  "fc.titleGradient": { es: "Maquillaje Gratis", en: "Face Chart Free" },
  "fc.subtitle": { es: "Plantilla de face chart profesional descargable en PNG. Elige el tipo de rostro y crea tu face chart online gratis.", en: "Professional downloadable face chart template in PNG. Choose the face type and create your face chart online for free." },
  "fc.aiTitle": { es: "Generar con IA", en: "Generate with AI" },
  "fc.aiDesc": { es: "Crea face charts únicos en blanco y negro con inteligencia artificial. Elige el tipo de rostro y genera tu plantilla.", en: "Create unique black and white face charts with artificial intelligence. Choose the face type and generate your template." },
  "fc.generating": { es: "Generando…", en: "Generating…" },
  "fc.generate": { es: "Generar Face Chart", en: "Generate Face Chart" },
  "fc.download": { es: "Descargar Face Chart", en: "Download Face Chart" },
  "fc.watermark": { es: "Descarga sin marca de agua con Premium", en: "Download without watermark with Premium" },
  "fc.ovalado": { es: "Ovalado", en: "Oval" },
  "fc.redondo": { es: "Redondo", en: "Round" },
  "fc.corazon": { es: "Corazón", en: "Heart" },
  "fc.cuadrado": { es: "Cuadrado", en: "Square" },
  "fc.alargado": { es: "Alargado", en: "Oblong" },
  "fc.diamante": { es: "Diamante", en: "Diamond" },
  "fc.triangulo": { es: "Triángulo", en: "Triangle" },
  "fc.trianguloInv": { es: "Triángulo Invertido", en: "Inverted Triangle" },
  "fc.tplOval": { es: "Rostro Ovalado", en: "Oval Face" },
  "fc.tplOvalDesc": { es: "Facciones proporcionadas, frente amplia, mentón suave", en: "Proportionate features, broad forehead, soft chin" },
  "fc.tplRound": { es: "Rostro Redondo", en: "Round Face" },
  "fc.tplRoundDesc": { es: "Mejillas prominentes, mandíbula suave, rasgos dulces", en: "Prominent cheeks, soft jawline, sweet features" },
  "fc.tplHeart": { es: "Rostro Corazón", en: "Heart Face" },
  "fc.tplHeartDesc": { es: "Frente ancha, mentón en punta, pómulos marcados", en: "Wide forehead, pointed chin, defined cheekbones" },
  "fc.tplSquare": { es: "Rostro Cuadrado", en: "Square Face" },
  "fc.tplSquareDesc": { es: "Mandíbula definida, frente ancha, rasgos fuertes", en: "Defined jawline, wide forehead, strong features" },
  "fc.tplOblong": { es: "Rostro Alargado", en: "Oblong Face" },
  "fc.tplOblongDesc": { es: "Proporciones verticales, facciones elegantes y estilizadas", en: "Vertical proportions, elegant and stylized features" },
  "fc.aiGenerated": { es: "Generado con IA · Blanco y negro · Sin maquillaje", en: "AI Generated · Black and white · No makeup" },
  "fc.generated": { es: "¡Face chart generado!", en: "Face chart generated!" },
  "fc.generatedDesc": { es: "Plantilla de rostro {label} creada con IA.", en: "{label} face template created with AI." },
  "fc.errorGen": { es: "Error al generar", en: "Error generating" },
  "fc.tryAgain": { es: "Intenta de nuevo", en: "Try again" },
  "fc.prev": { es: "Anterior", en: "Previous" },
  "fc.next": { es: "Siguiente", en: "Next" },
  "fc.saveGallery": { es: "Guardar en Galería", en: "Save to Gallery" },
  "fc.saved": { es: "¡Guardado!", en: "Saved!" },
  "fc.savedDesc": { es: "El face chart se guardó en tu galería.", en: "The face chart was saved to your gallery." },
  "fc.saveLoginRequired": { es: "Inicia sesión para guardar en la galería", en: "Sign in to save to gallery" },

  // Analyzer
  "an.title": { es: "Analizador de Maquillaje", en: "Online Makeup" },
  "an.titleGradient": { es: "Online", en: "Analyzer" },
  "an.subtitle": { es: "Sube tu foto de maquillaje y obtén una puntuación detallada. Analiza maquillaje con foto y detecta mejoras en tu look.", en: "Upload your makeup photo and get a detailed score. Analyze makeup with a photo and detect improvements in your look." },
  "an.todayCount": { es: "Análisis hoy:", en: "Today's analyses:" },
  "an.upgradePlan": { es: "Mejora tu plan", en: "Upgrade your plan" },
  "an.limitReached": { es: "Límite alcanzado", en: "Limit reached" },
  "an.limitReachedDesc": { es: "Tu plan permite {limit} análisis al día. Mejora tu plan para más.", en: "Your plan allows {limit} analyses per day. Upgrade for more." },
  "an.dailyLimitTitle": { es: "Límite diario alcanzado", en: "Daily limit reached" },
  "an.dailyLimitDesc": { es: "Has usado tus {limit} análisis de hoy. Mejora tu plan para desbloquear más análisis.", en: "You've used your {limit} analyses today. Upgrade your plan to unlock more." },
  "an.viewPlans": { es: "Ver Planes", en: "View Plans" },
  "an.dragOrClick": { es: "Arrastra o haz clic para subir", en: "Drag or click to upload" },
  "an.formats": { es: "JPG, PNG o WEBP", en: "JPG, PNG or WEBP" },
  "an.changeImage": { es: "Cambiar Imagen", en: "Change Image" },
  "an.analyzing": { es: "Analizando con IA...", en: "Analyzing with AI..." },
  "an.analyze": { es: "Analizar", en: "Analyze" },
  "an.blending": { es: "Difuminado", en: "Blending" },
  "an.symmetry": { es: "Simetría", en: "Symmetry" },
  "an.colorHarmony": { es: "Armonía de Color", en: "Color Harmony" },
  "an.coverage": { es: "Cobertura", en: "Coverage" },
  "an.creativity": { es: "Creatividad", en: "Creativity" },
  "an.aiRecs": { es: "Recomendaciones de IA", en: "AI Recommendations" },
  "an.recBlending": { es: "Trabaja más el difuminado en las transiciones de color", en: "Work more on blending in color transitions" },
  "an.recSymmetry": { es: "Practica la simetría usando puntos de referencia faciales", en: "Practice symmetry using facial reference points" },
  "an.recColor": { es: "Explora la rueda de color para combinaciones más armoniosas", en: "Explore the color wheel for more harmonious combinations" },
  "an.recCoverage": { es: "Mejora la cobertura aplicando capas finas y uniformes", en: "Improve coverage by applying thin, even layers" },
  "an.recCreativity": { es: "Experimenta con técnicas y estilos nuevos para expandir tu creatividad", en: "Experiment with new techniques and styles to expand your creativity" },
  "an.recExcellent": { es: "¡Excelente trabajo! Sigue perfeccionando tu técnica", en: "Excellent work! Keep perfecting your technique" },
  "an.errorAnalysis": { es: "Error en el análisis", en: "Analysis error" },
  "an.errorDesc": { es: "No se pudo analizar la imagen. Intenta de nuevo.", en: "Could not analyze the image. Try again." },
  "an.levelExpert": { es: "Experta Profesional", en: "Professional Expert" },
  "an.levelAdvanced": { es: "Avanzada", en: "Advanced" },
  "an.levelIntermediate": { es: "Intermedia", en: "Intermediate" },
  "an.levelBeginner": { es: "Principiante", en: "Beginner" },

  // Quiz
  "quiz.title": { es: "Quiz de", en: "Makeup" },
  "quiz.titleGradient": { es: "Maquillaje", en: "Quiz" },
  "quiz.questionCount": { es: "{count} preguntas sobre técnicas, productos y tendencias", en: "{count} questions about techniques, products and trends" },
  "quiz.testKnowledge": { es: "Pon a prueba tus conocimientos de belleza", en: "Test your beauty knowledge" },
  "quiz.start": { es: "Comenzar Quiz", en: "Start Quiz" },
  "quiz.question": { es: "Pregunta", en: "Question" },
  "quiz.of": { es: "de", en: "of" },
  "quiz.explanation": { es: "Explicación:", en: "Explanation:" },
  "quiz.next": { es: "Siguiente", en: "Next" },
  "quiz.viewResults": { es: "Ver Resultados", en: "View Results" },
  "quiz.correct": { es: "Correctas", en: "Correct" },
  "quiz.incorrect": { es: "Incorrectas", en: "Incorrect" },
  "quiz.retry": { es: "Intentar de Nuevo", en: "Try Again" },
  "quiz.lvl1": { es: "👑 Maestra del Maquillaje", en: "👑 Makeup Master" },
  "quiz.lvl1Msg": { es: "¡Increíble! Dominas el arte del maquillaje como una profesional.", en: "Incredible! You master the art of makeup like a professional." },
  "quiz.lvl2": { es: "💄 Experta Avanzada", en: "💄 Advanced Expert" },
  "quiz.lvl2Msg": { es: "¡Muy bien! Tienes un conocimiento sólido. Sigue perfeccionándote.", en: "Great! You have solid knowledge. Keep perfecting yourself." },
  "quiz.lvl3": { es: "✨ En Camino", en: "✨ On the Way" },
  "quiz.lvl3Msg": { es: "Buen trabajo. Estás en el camino correcto, sigue aprendiendo.", en: "Good job. You're on the right track, keep learning." },
  "quiz.lvl4": { es: "🌱 Principiante Curiosa", en: "🌱 Curious Beginner" },
  "quiz.lvl4Msg": { es: "¡No te desanimes! Cada experta empezó desde cero. Sigue explorando.", en: "Don't get discouraged! Every expert started from zero. Keep exploring." },

  // Blog
  "blog.title": { es: "Blog de", en: "Makeup" },
  "blog.titleGradient": { es: "Maquillaje", en: "Blog" },
  "blog.subtitle": { es: "Tendencias de maquillaje, face charts, maquillaje coreano y guías paso a paso", en: "Makeup trends, face charts, Korean makeup and step-by-step guides" },
  "blog.read": { es: "Leer", en: "Read" },
  "blog.moreArticles": { es: "artículos", en: "articles" },
  "blog.availableWith": { es: "Disponibles con el plan Premium o Plus", en: "Available with Premium or Plus plan" },
  "blog.viewPlans": { es: "Ver Planes", en: "View Plans" },
  "blog.notFound": { es: "Artículo no encontrado", en: "Article not found" },
  "blog.backToBlog": { es: "Volver al blog", en: "Back to blog" },

  // Auth
  "auth.loginTab": { es: "Iniciar Sesión", en: "Sign In" },
  "auth.registerTab": { es: "Registrarse", en: "Sign Up" },
  "auth.resetTab": { es: "Recuperar", en: "Recover" },
  "auth.continueGoogle": { es: "Continuar con Google", en: "Continue with Google" },
  "auth.orEmail": { es: "o con correo", en: "or with email" },
  "auth.email": { es: "Correo electrónico", en: "Email" },
  "auth.password": { es: "Contraseña", en: "Password" },
  "auth.fullName": { es: "Nombre completo", en: "Full name" },
  "auth.passwordMin": { es: "Contraseña (mínimo 6 caracteres)", en: "Password (minimum 6 characters)" },
  "auth.signInBtn": { es: "Iniciar Sesión", en: "Sign In" },
  "auth.createAccountBtn": { es: "Crear Cuenta", en: "Create Account" },
  "auth.sendLink": { es: "Enviar Enlace", en: "Send Link" },
  "auth.loading": { es: "Cargando...", en: "Loading..." },
  "auth.resendConfirmation": { es: "Reenviar correo de confirmación", en: "Resend confirmation email" },
  "auth.resetDesc": { es: "Ingresa tu correo electrónico y te enviaremos un enlace para restablecer tu contraseña.", en: "Enter your email and we'll send you a link to reset your password." },
  "auth.welcome": { es: "¡Bienvenida!", en: "Welcome!" },
  "auth.welcomeDesc": { es: "Has iniciado sesión correctamente.", en: "You have signed in successfully." },
  "auth.registerSuccess": { es: "¡Registro exitoso!", en: "Registration successful!" },
  "auth.registerConfirm": { es: "Te enviamos un correo de confirmación. Revisa también Spam/Promociones.", en: "We sent you a confirmation email. Also check Spam/Promotions." },
  "auth.registerActive": { es: "Tu cuenta quedó activa y ya puedes iniciar sesión.", en: "Your account is active and you can now sign in." },
  "auth.emailSent": { es: "Correo enviado", en: "Email sent" },
  "auth.emailSentDesc": { es: "Revisa tu bandeja de entrada para restablecer tu contraseña.", en: "Check your inbox to reset your password." },
  "auth.enterEmail": { es: "Ingresa tu correo", en: "Enter your email" },
  "auth.enterEmailDesc": { es: "Escribe tu correo y luego presiona reenviar confirmación.", en: "Type your email and then press resend confirmation." },
  "auth.resendSuccess": { es: "Correo de confirmación reenviado", en: "Confirmation email resent" },
  "auth.resendSuccessDesc": { es: "Revisa Bandeja principal, Spam y Promociones.", en: "Check Inbox, Spam and Promotions." },
  "auth.resendFail": { es: "No se pudo reenviar el correo", en: "Could not resend the email" },
  "auth.resendFailDesc": { es: "Intenta nuevamente en 1 minuto o usa otra dirección de correo.", en: "Try again in 1 minute or use another email address." },
  "auth.errorGoogle": { es: "Error con Google", en: "Google error" },

  // Premium
  "pm.title1": { es: "Elige tu plan", en: "Choose your" },
  "pm.title2": { es: "ideal", en: "ideal plan" },
  "pm.subtitle": { es: "Desde lo básico hasta lo ilimitado. Encuentra el plan que se adapte a tu nivel.", en: "From basic to unlimited. Find the plan that fits your level." },
  "pm.free": { es: "Gratis", en: "Free" },
  "pm.freeDesc": { es: "Perfecto para empezar a aprender.", en: "Perfect to start learning." },
  "pm.premiumDesc": { es: "Desbloquea herramientas profesionales.", en: "Unlock professional tools." },
  "pm.plusDesc": { es: "Todo ilimitado. Sin restricciones.", en: "Everything unlimited. No restrictions." },
  "pm.popular": { es: "Popular", en: "Popular" },
  "pm.bestValue": { es: "Mejor Valor", en: "Best Value" },
  "pm.currentPlan": { es: "Plan Actual", en: "Current Plan" },
  "pm.included": { es: "Incluido", en: "Included" },
  "pm.signIn": { es: "Inicia Sesión", en: "Sign In" },
  "pm.subscribeStripe": { es: "Suscribirse con Stripe", en: "Subscribe with Stripe" },
  "pm.comparison": { es: "Comparación", en: "Detailed" },
  "pm.comparisonGradient": { es: "detallada", en: "Comparison" },
  "pm.function": { es: "Función", en: "Feature" },
  "pm.manageSubscription": { es: "Administrar suscripción", en: "Manage subscription" },
  "pm.manageDesc": { es: "Contacta soporte para cambios en tu plan.", en: "Contact support for plan changes." },
  "pm.paymentComplete": { es: "Pago completado", en: "Payment completed" },
  "pm.paymentSync": { es: "Tu suscripción se está sincronizando.", en: "Your subscription is syncing." },
  "pm.checkoutCanceled": { es: "Checkout cancelado", en: "Checkout canceled" },
  "pm.noCargo": { es: "No se realizó ningún cobro.", en: "No charge was made." },
  "pm.securePayments": { es: "Pagos Seguros", en: "Secure Payments" },
  "pm.secureDesc": { es: "Procesado con Stripe, seguridad de nivel bancario.", en: "Processed with Stripe, bank-level security." },
  "pm.instantAccess": { es: "Acceso Inmediato", en: "Instant Access" },
  "pm.instantDesc": { es: "Desbloquea tus beneficios al instante tras el pago.", en: "Unlock your benefits instantly after payment." },
  // Feature labels
  "pm.blogArticles": { es: "Artículos del blog", en: "Blog articles" },
  "pm.historyAccess": { es: "Historia del maquillaje", en: "Makeup history" },
  "pm.analysisPerDay": { es: "Análisis de maquillaje", en: "Makeup analysis" },
  "pm.quizAccess": { es: "Quiz interactivo", en: "Interactive quiz" },
  "pm.quizHistory": { es: "Historial de quizzes", en: "Quiz history" },
  "pm.downloadFC": { es: "Descargar Face Charts", en: "Download Face Charts" },
  "pm.earlyAccess": { es: "Acceso anticipado", en: "Early access" },
  "pm.support247": { es: "Soporte prioritario 24/7", en: "Priority 24/7 support" },
  "pm.all": { es: "Todos", en: "All" },
  "pm.perDay3": { es: "3 al día", en: "3 per day" },
  "pm.perDay5": { es: "5 al día", en: "5 per day" },
  "pm.unlimited": { es: "Ilimitados", en: "Unlimited" },
  "pm.withWatermark": { es: "Con marca de agua", en: "With watermark" },
  "pm.noWatermark": { es: "Sin marca", en: "No watermark" },

  // Profile
  "profile.loading": { es: "Cargando...", en: "Loading..." },
  "profile.signInToView": { es: "Inicia sesión para ver tu perfil", en: "Sign in to view your profile" },
  "profile.signInDesc": { es: "Crea una cuenta o inicia sesión para acceder a tu historial y actividad.", en: "Create an account or sign in to access your history and activity." },
  "profile.goToSignIn": { es: "Ir a Iniciar Sesión", en: "Go to Sign In" },
  "profile.save": { es: "Guardar", en: "Save" },
  "profile.cancel": { es: "Cancelar", en: "Cancel" },
  "profile.user": { es: "Usuario", en: "User" },
  "profile.signOut": { es: "Cerrar Sesión", en: "Sign Out" },
  "profile.quizzesCompleted": { es: "Quizzes Completados", en: "Quizzes Completed" },
  "profile.faceChartsSaved": { es: "Face Charts Guardados", en: "Face Charts Saved" },
  "profile.analysesCompleted": { es: "Análisis Realizados", en: "Analyses Done" },
  "profile.quizHistory": { es: "Historial de Quizzes", en: "Quiz History" },
  "profile.noQuizzes": { es: "Aún no has completado ningún quiz. ¡Ve a probarlo!", en: "You haven't completed any quizzes yet. Go try it!" },
  "profile.analyzerHistory": { es: "Historial de Análisis", en: "Analysis History" },
  "profile.noAnalyses": { es: "Aún no has analizado ninguna imagen. ¡Prueba el analizador!", en: "You haven't analyzed any images yet. Try the analyzer!" },
  "profile.updated": { es: "¡Actualizado!", en: "Updated!" },
  "profile.nameUpdated": { es: "Tu nombre se ha actualizado.", en: "Your name has been updated." },

  // Gallery
  "gallery.title": { es: "Mi", en: "My" },
  "gallery.titleGradient": { es: "Galería", en: "Gallery" },
  "gallery.subtitle": { es: "Tu historial de face charts y análisis de maquillaje", en: "Your face charts and makeup analysis history" },
  "gallery.signInTitle": { es: "Galería", en: "Gallery" },
  "gallery.signInDesc": { es: "Inicia sesión para ver tu historial de face charts y análisis.", en: "Sign in to see your face charts and analysis history." },
  "gallery.signIn": { es: "Iniciar Sesión", en: "Sign In" },
  "gallery.faceCharts": { es: "Face Charts", en: "Face Charts" },
  "gallery.analysis": { es: "Análisis", en: "Analyses" },
  "gallery.noCharts": { es: "Aún no has guardado ningún face chart", en: "You haven't saved any face charts yet" },
  "gallery.createFC": { es: "Crear Face Chart", en: "Create Face Chart" },
  "gallery.noAnalysis": { es: "Aún no has analizado ningún maquillaje", en: "You haven't analyzed any makeup yet" },
  "gallery.analyzeBtn": { es: "Analizar Maquillaje", en: "Analyze Makeup" },
  "gallery.delete": { es: "Eliminar", en: "Delete" },
  "gallery.deleteError": { es: "Error al eliminar", en: "Error deleting" },
  "gallery.deleted": { es: "Face chart eliminado", en: "Face chart deleted" },
  "gallery.blending": { es: "Difuminado", en: "Blending" },
  "gallery.symmetry": { es: "Simetría", en: "Symmetry" },
  "gallery.color": { es: "Color", en: "Color" },
  "gallery.coverage": { es: "Cobertura", en: "Coverage" },
  "gallery.creativity": { es: "Creatividad", en: "Creativity" },

  // History
  "history.title": { es: "Historia del", en: "History of" },
  "history.titleGradient": { es: "Maquillaje", en: "Makeup" },
  "history.subtitle": { es: "Un viaje fascinante de más de 5.000 años a través de las civilizaciones, los iconos y las revoluciones que moldearon el arte de la belleza", en: "A fascinating journey of over 5,000 years through civilizations, icons and revolutions that shaped the art of beauty" },
  "history.curiosities": { es: "Curiosidades", en: "Fascinating" },
  "history.curiositiesGradient": { es: "Fascinantes", en: "Facts" },

  // ResetPassword
  "reset.title": { es: "Nueva Contraseña", en: "New Password" },
  "reset.subtitle": { es: "Escribe tu nueva contraseña para restablecer tu acceso.", en: "Enter your new password to restore your access." },
  "reset.invalidLink": { es: "Este enlace no es válido o ha expirado. Por favor solicita un nuevo enlace de recuperación.", en: "This link is invalid or has expired. Please request a new recovery link." },
  "reset.goToSignIn": { es: "Ir a Iniciar Sesión", en: "Go to Sign In" },
  "reset.newPassword": { es: "Nueva contraseña (mínimo 6 caracteres)", en: "New password (minimum 6 characters)" },
  "reset.confirmPassword": { es: "Confirmar contraseña", en: "Confirm password" },
  "reset.updateBtn": { es: "Actualizar Contraseña", en: "Update Password" },
  "reset.loading": { es: "Cargando...", en: "Loading..." },
  "reset.mismatch": { es: "Las contraseñas no coinciden.", en: "Passwords don't match." },
  "reset.tooShort": { es: "La contraseña debe tener al menos 6 caracteres.", en: "Password must be at least 6 characters." },
  "reset.success": { es: "¡Contraseña actualizada!", en: "Password updated!" },
  "reset.successDesc": { es: "Ya puedes iniciar sesión con tu nueva contraseña.", en: "You can now sign in with your new password." },
  "reset.invalidLinkToast": { es: "Enlace inválido", en: "Invalid link" },
  "reset.invalidLinkDesc": { es: "Solicita un nuevo enlace de recuperación.", en: "Request a new recovery link." },

  // NotFound
  "notFound.title": { es: "Página no encontrada", en: "Page not found" },
  "notFound.back": { es: "Volver al Inicio", en: "Return to Home" },

  // PlanGate
  "planGate.exclusive": { es: "Función exclusiva del plan", en: "Exclusive feature of the" },
  "planGate.availableFrom": { es: "está disponible a partir del plan", en: "is available from the" },
  "planGate.upgradeDesc": { es: "Mejora tu plan para desbloquear esta y muchas más funciones.", en: "Upgrade your plan to unlock this and many more features." },
  "planGate.viewPlans": { es: "Ver Planes", en: "View Plans" },

  // Common
  "common.error": { es: "Error", en: "Error" },
  "common.planGratis": { es: "Plan Gratis", en: "Free Plan" },
};

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(() => {
    const saved = localStorage.getItem("glamcanvas-lang");
    return (saved === "en" || saved === "es") ? saved : "es";
  });

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem("glamcanvas-lang", lang);
  };

  const t = (key: string): string => {
    const entry = translations[key];
    if (!entry) return key;
    return entry[language] ?? entry.es ?? key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used within LanguageProvider");
  return ctx;
}
